// Breadcrumb — a projection of the resolved route + the policy, drawn by the design system's
// `Breadcrumbs`. The root "Home" crumb appears only when the persona can reach the dashboard.
//
// This is the ONE breadcrumb: it is rendered by the shell above every page, so a page that draws its
// own would sit under a second trail rather than replace it. A page that goes deeper adds its crumbs
// here instead.
//
// A crumb carries the route it opens, not just a kind, so a trail can step through a parameterised
// place (a node, then that node's Services tab) — which is what makes it able to mirror a nested URL
// rather than approximate it.
//
// The trail follows the URL segment for segment, sub-tabs included: #/cluster/member/hotrod/services
// reads "Home / Cluster / Hotrod / Services", and every crumb but the last opens the prefix of the
// URL it sits at. A tab crumb appears only for a tab the URL actually names — a route's default tab
// is omitted from the hash and so has no crumb, and a stale or mistyped segment (which the page
// resolves back to its default) is left off rather than announced.
//
// One segment is deliberately silent: the `member` word that names a member. It is a namespace
// rather than a place — there is nothing at #/cluster/member — and today the list of members IS the
// Cluster page, so a crumb there would repeat the link beside it. It earns one the day
// #/cluster/members becomes a page of its own.

import { Breadcrumbs } from "@thekrystalship/krystal";
import { KrystalRouter } from "../lib/router.js";
import { can, nodeTabOffered } from "../lib/persona.js";
import { KRYSTAL_LABELS, anchorOffersTab, tabLabel } from "../lib/labels.js";

function breadcrumbTrail(route, ctx) {
  const trail = [];
  if (can("nav.dashboard")) trail.push({ label: KRYSTAL_LABELS.home, to: { kind: "home" } });
  // The tab crumb every tabbed route ends with. `to` is left off: it is the last crumb, and the
  // last crumb is where you already are.
  const tab = (kind, offered = true) => {
    const label = offered ? tabLabel(kind, route.tab) : null;
    if (label) trail.push({ label });
  };
  switch (route.kind) {
    case "home":      break;
    case "servers":   trail.push({ label: KRYSTAL_LABELS.servers, to: { kind: "servers" } }); break;
    case "server":    trail.push(
                        { label: KRYSTAL_LABELS.servers, to: { kind: "servers" } },
                        { label: ctx.serverName || "Server", to: { kind: "server", id: route.id } });
                      // A tab whose read this person lacks is hidden and lands them on the overview —
                      // so there is no crumb to add.
                      tab("server", route.tab === "overview" || ctx.serverTabOffered);
                      break;
    case "library":   trail.push({ label: ctx.catalogLabel }); break;
    case "library-create": trail.push({ label: ctx.catalogLabel, to: { kind: "library" } }, { label: "New blueprint" }); break;
    case "game":      trail.push(
                        { label: ctx.catalogLabel, to: { kind: "library" } },
                        { label: ctx.gameName || "Game", to: { kind: "game", id: route.id } });
                      tab("game");
                      break;
    case "attention": trail.push({ label: KRYSTAL_LABELS.alerts }); break;
    case "audit":     trail.push({ label: KRYSTAL_LABELS.audit }); break;
    case "addHost":   trail.push({ label: KRYSTAL_LABELS.cluster, to: { kind: "cluster" } }, { label: "Add a host" }); break;
    // One route for one member, and a member is a node or an anchor. The trail is the same either
    // way; only which tab strip names the tab differs, because the two members offer different ones.
    case "cluster":   if (route.hostId) {
                        trail.push(
                          { label: KRYSTAL_LABELS.cluster, to: { kind: "cluster" } },
                          { label: ctx.hostName || route.hostId, to: { kind: "cluster", hostId: route.hostId } });
                        // An anchor's strip is its CAPABILITY's, so a tab it does not serve
                        // resolves back to the overview on the page — and a crumb naming it would
                        // announce a place that is not on screen.
                        // A node's tab this person holds nothing for is left out the same way.
                        tab(ctx.memberKind === "anchor" ? "anchor" : "cluster",
                          ctx.memberKind === "anchor"
                            ? anchorOffersTab(ctx.memberCapability, route.tab)
                            : nodeTabOffered(route.hostId, route.tab || "overview"));
                      } else if (route.tab) {
                        // The cluster's own tabs. One crumb deeper than the bare page, and named
                        // from its own strip rather than a member's — the two share the URL word
                        // and nothing else.
                        trail.push({ label: KRYSTAL_LABELS.cluster, to: { kind: "cluster" } });
                        tab("clusterRoot");
                      } else {
                        trail.push({ label: KRYSTAL_LABELS.cluster });
                      }
                      break;
    // The leaf page hangs off its node's Services tab, and the trail says so — each crumb opens the
    // place its word names, so walking back up the breadcrumb walks back up the URL.
    case "leaf":        trail.push(
                        { label: KRYSTAL_LABELS.cluster, to: { kind: "cluster" } },
                        { label: ctx.hostName || route.hostId || "Host", to: { kind: "cluster", hostId: route.hostId } },
                        { label: "Services", to: { kind: "cluster", hostId: route.hostId, tab: "services" } },
                        // The leaf's display name comes from the services board; until that answers, its
                        // id is what we actually know — shown as-is rather than held blank.
                        { label: ctx.leafName || route.leaf || "Leaf", to: { kind: "leaf", hostId: route.hostId, leaf: route.leaf } });
                      tab("leaf");
                      break;
    // The configuration page's tabs are the node's leaves, so the crumb after "Configuration" is the
    // leaf being configured — named by the services board, falling back to the id in the URL.
    case "leafConfig":  trail.push(
                        { label: KRYSTAL_LABELS.cluster, to: { kind: "cluster" } },
                        { label: ctx.hostName || route.hostId || "Host", to: { kind: "cluster", hostId: route.hostId } },
                        route.leaf
                          ? { label: "Configuration", to: { kind: "leafConfig", hostId: route.hostId } }
                          : { label: "Configuration" });
                      if (route.leaf) trail.push({ label: ctx.leafName || route.leaf });
                      break;
    case "assistant": trail.push({ label: KRYSTAL_LABELS.assistant }); break;
    case "notifications": trail.push({ label: KRYSTAL_LABELS.notifications }); break;
    case "profile":   trail.push({ label: KRYSTAL_LABELS.profile, to: { kind: "profile" } }); tab("profile"); break;
    case "settings":  trail.push({ label: KRYSTAL_LABELS.settings, to: { kind: "settings" } }); tab("settings"); break;
    default:          break;
  }
  return trail;
}

function Breadcrumb({ route, ctx, onNavigate }) {
  // Each ancestor is a real `#/...` link, the serialization the router writes into the address bar,
  // so middle-click and copy-link land where a plain click does. A plain click is mapped back from
  // the href to the crumb's route object, which is what the shell's `setRoute` takes.
  const opens = new Map();
  const items = breadcrumbTrail(route, ctx).map((c) => {
    if (!c.to) return { label: c.label };
    const href = KrystalRouter.routeToHash(c.to);
    opens.set(href, c.to);
    return { label: c.label, href };
  });
  return (
    <Breadcrumbs
      items={items}
      onNavigate={(href) => { const to = opens.get(href); if (to) onNavigate(to); }} />
  );
}

export { Breadcrumb };
